import { faTelescope } from '@fortawesome/pro-duotone-svg-icons';
import React, { Dispatch, FC, SetStateAction } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { FormTemplate } from 'api/form/types';
import { Button } from 'components/atoms/button';
import { Drawer as DrawerAtom } from 'components/atoms/drawer';
import {
  EmptyState,
  EmptyStateContainer as EmptyStateContainerAtom,
  EmptyStateIcon,
} from 'components/atoms/empty-state';
import { FormSection } from 'components/molecules/forms/form-fields/styles';
import { FormQuestionRenderer } from 'components/organisms/forms';
import { BodyShort01, ProductiveHeading05, Text01 } from 'components/styles';
import { useDeleteConfirmModal } from 'hooks/use-delete-confirm-modal';
import { useTranslate } from 'hooks/use-translation';

const EmptyStateContainer = styled(EmptyStateContainerAtom)`
  && {
    text-align: left;
    padding: var(--spacing-11) 0px;
  }
`;

const Header = styled.div`
  background: var(--task-pane-header-bg);
  padding: var(--spacing-05) var(--spacing-07);
  height: 88px;
  align-items: flex-end;
  display: flex;
`;
const Content = styled.div`
  padding: 0 var(--spacing-06) 150px;
  height: 100%;
  overflow-y: scroll;
`;
const HeaderTitle = styled.div`
  ${ProductiveHeading05};
  ${Text01};
`;
const Drawer = styled(DrawerAtom).attrs({
  width: 648,
  closable: true,
  placement: 'right',
})`
  .ant-drawer-body {
    padding: 0;
    overflow: hidden;
  }
`;
const FormDesc = styled.div`
  ${BodyShort01};
  color: var(--gray-80);
  padding: var(--spacing-07) 0 0;
`;
const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  ${Button}:disabled {
    pointer-events: none;
    opacity: 0.5;
  }
  & button:not(:first-child) {
    margin-left: var(--spacing-03);
  }
`;
const FormPreview = styled.form`
  padding-top: var(--spacing-07);
  section {
    ${FormSection};
    margin-bottom: var(--spacing-05);
  }
`;

export const FormPanePreview: FC<{
  formTemplate: FormTemplate;
  showFormPane: boolean;
  setShowFormPane: Dispatch<SetStateAction<boolean>>;
}> = ({ formTemplate, showFormPane, setShowFormPane }) => {
  const { control, setValue, reset } = useForm();

  const translate = useTranslate();
  const clearFormConfirmation = useDeleteConfirmModal({
    title: `${translate('clear_form')}?`,
    content: translate('forms.response.clear_form_message'),
    okText: translate('clear'),
    onOk: () => {
      reset();
    },
  });

  return (
    <Drawer
      visible={showFormPane}
      onClose={() => setShowFormPane(false)}
      destroyOnClose
      footer={
        <ButtonGroup>
          <Button onClick={clearFormConfirmation}>
            <FormattedMessage id="clear_form" />
          </Button>
          <Button type="primary" disabled>
            <FormattedMessage id="submit" />
          </Button>
        </ButtonGroup>
      }
    >
      <Header>
        <HeaderTitle>{formTemplate.name}</HeaderTitle>
      </Header>
      <Content>
        {formTemplate.description && (
          <FormDesc>{formTemplate.description}</FormDesc>
        )}
        {formTemplate.formQuestions.length ? (
          <FormPreview>
            {formTemplate.formQuestions.map((question) => (
              <FormQuestionRenderer
                question={question}
                control={control}
                setValue={setValue}
                disabled={false}
              />
            ))}
          </FormPreview>
        ) : (
          <EmptyStateContainer>
            <EmptyState
              image={<EmptyStateIcon icon={faTelescope} />}
              title={<FormattedMessage id="forms.response.no_question.title" />}
              message={
                <FormattedMessage id="forms.response.no_question.message" />
              }
              secondaryCTA={null}
            />
          </EmptyStateContainer>
        )}
      </Content>
    </Drawer>
  );
};
